import { useCallback, useEffect, useState } from "react";

import {
  fetchPresaleRoundLimitsFromChain,
  GEMS_ROUND_USD_CAP,
  SPECIAL_ROUND_USD_CAP,
  REGULAR_INVESTED_THRESHOLD_USD,
  REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
  REGULAR_BULK_DISCOUNT_RATE,
  regularBulkDiscountPercentFromRate,
} from "../../utils/presaleRoundLimits";

/**
 * Reads on-chain presale limits and per-wallet USD counters.
 * USD fields use 6 decimals; gems round cap / sold use 18 decimals (wei kept as strings).
 */
export default function usePresaleRoundLimits(account) {
  const [gemsSpentUsd, setGemsSpentUsd] = useState(0);
  const [specialSpentUsd, setSpecialSpentUsd] = useState(0);
  const [regularInvestedUsd, setRegularInvestedUsd] = useState(0);
  const [gemsRoundCapWei, setGemsRoundCapWei] = useState(null);
  const [totalGemsRoundSoldWei, setTotalGemsRoundSoldWei] = useState(null);
  const [gemsRoundUsdCap, setGemsRoundUsdCap] = useState(GEMS_ROUND_USD_CAP);
  const [specialRoundUsdCap, setSpecialRoundUsdCap] = useState(
    SPECIAL_ROUND_USD_CAP,
  );
  const [regularInvestedThresholdUsd, setRegularInvestedThresholdUsd] =
    useState(REGULAR_INVESTED_THRESHOLD_USD);
  const [regularMinOrderUsd, setRegularMinOrderUsd] = useState(
    REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
  );
  const [regularBulkDiscountRate, setRegularBulkDiscountRate] = useState(
    REGULAR_BULK_DISCOUNT_RATE,
  );
  const [regularBulkDiscountPercent, setRegularBulkDiscountPercent] =
    useState(regularBulkDiscountPercentFromRate(REGULAR_BULK_DISCOUNT_RATE));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetchPresaleLimits = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const limits = await fetchPresaleRoundLimitsFromChain({ account });

      setGemsRoundCapWei(limits.gemsRoundCapWei);
      setTotalGemsRoundSoldWei(limits.totalGemsRoundSoldWei);

      if (limits.gemsRoundUsdCap > 0) {
        setGemsRoundUsdCap(limits.gemsRoundUsdCap);
      }
      if (limits.specialRoundUsdCap > 0) {
        setSpecialRoundUsdCap(limits.specialRoundUsdCap);
      }
      if (limits.regularInvestedThresholdUsd > 0) {
        setRegularInvestedThresholdUsd(limits.regularInvestedThresholdUsd);
      }
      if (limits.regularMinOrderUsd > 0) {
        setRegularMinOrderUsd(limits.regularMinOrderUsd);
      }
      if (limits.regularBulkDiscountRate > 0) {
        setRegularBulkDiscountRate(limits.regularBulkDiscountRate);
      }
      if (limits.regularBulkDiscountPercent > 0) {
        setRegularBulkDiscountPercent(limits.regularBulkDiscountPercent);
      }

      setGemsSpentUsd(limits.gemsSpentUsd);
      setSpecialSpentUsd(limits.specialSpentUsd);
      setRegularInvestedUsd(limits.regularInvestedUsd);
    } catch (e) {
      console.warn("usePresaleRoundLimits:", e?.message || e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    refetchPresaleLimits();

    const t = setInterval(refetchPresaleLimits, 25000);

    return () => clearInterval(t);
  }, [refetchPresaleLimits]);

  return {
    gemsSpentUsd,
    specialSpentUsd,
    regularInvestedUsd,
    gemsRoundCapWei,
    totalGemsRoundSoldWei,
    gemsRoundUsdCap,
    specialRoundUsdCap,
    regularInvestedThresholdUsd,
    regularMinOrderUsd,
    regularBulkDiscountRate,
    regularBulkDiscountPercent,
    loading,
    error,
    refetchPresaleLimits,
  };
}
